import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Topbar from './components/Topbar.js';
import Home from './pages/Home.js';
import AdminPage from './pages/AdminPage.jsx';
import DNViewer from './pages/DNViewer.jsx';
import VisitsViewer from './pages/VisitsViewer.jsx';
import TelematicViewer from './pages/TelematicViewer.jsx';
import GICampaignViewer from './pages/GICampaignViewer.jsx';
import GIUnansweredSurveys from './pages/GIUnansweredSurveys.jsx';
import GIAIReportViewer from './pages/GIAIReportViewer.jsx';
import GIPlanningViewer from './pages/GIPlanningViewer.jsx';
import StatisticsViewer from './pages/StatisticsViewer.jsx';
import GPSViewer from './pages/GPSViewer.jsx';
import InterventionPointViewer from './pages/InterventionPointViewer.jsx';
import AIQueryViewer from './pages/AIQueryViewer.jsx';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID, COOKIE_DOMAIN } from './config.js';
import useAnalytics from './hooks/useAnalytics.js';
import baseFetchWithAuth, { setSessionExpiredHandler } from './utils/fetchWithAuth.js';
import { isEmbedded, consumeConnectError, endAllSessions, startSessionWatch, isSessionLoss } from '@globegroupe-data/auth-kit/browser';

// Wrapper component to handle report context
const AppContent = ({ isAuthenticated, currentUser, logout, fetchWithAuth, authError }) => {

  // Utilisation du hook pour le tracking de base des changements de route
  useAnalytics({ currentUser: isAuthenticated ? currentUser : null });

  return (
    <div className="App h-full w-full bg-[#FAFAFA] dark:bg-[#0d0d0d] transition-colors duration-200 flex flex-col overflow-x-hidden">
      <Topbar
        currentUser={isAuthenticated ? currentUser : null}
        logout={isAuthenticated ? logout : null}
      />

      {!isAuthenticated ? (
        authError ? (
          <div className="max-w-md mx-auto mt-10 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            <p className="text-red-700 dark:text-red-300">{authError}</p>
          </div>
        ) : null
      ) : (
        <main className='w-full p-0 m-0 flex-1 overflow-y-auto min-h-0'>
          <Routes>
            <Route path="/" element={
              <Home
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/gps-viewer" element={
              <GPSViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/intervention-point-viewer" element={
              <InterventionPointViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/dn-viewer" element={
              <DNViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/visits-viewer" element={
              <VisitsViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/gi-campaign-viewer" element={
              <GICampaignViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/gi-unanswered-surveys" element={
              <GIUnansweredSurveys
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/telematic-viewer" element={
              <TelematicViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/ai-query-viewer" element={
              <AIQueryViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/gi-ai-report-viewer" element={
              <GIAIReportViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/gi-planning-viewer" element={
              <GIPlanningViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route path="/statistics-viewer" element={
              <StatisticsViewer
                currentUser={currentUser}
                fetchWithAuth={fetchWithAuth}
              />
            } />
            <Route
              path="/admin/*"
              element={
                <AdminPage
                  fetchWithAuth={fetchWithAuth}
                  currentUser={currentUser}
                />
              }
            />
          </Routes>
        </main>
      )}
    </div>
  );
};

function App() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Wrapper that returns the raw Response (legacy callers expect response.ok/response.json()).
  const fetchWithAuth = async (url, options = {}) => {
    return await baseFetchWithAuth(url, { ...options, returnRawResponse: true });
  };

  const logout = async () => {
    // Déconnexion = déconnexion : la session SSO tombe aussi, sinon les autres apps
    // resteraient connectées.
    await endAllSessions({
      apiBaseUrl: API_BASE_URL,
      connectUrl: CONNECT_UI_URL,
      clientId: CONNECT_CLIENT_ID,
    });

    setIsAuthenticated(false);
    setCurrentUser(null);
    setAuthError(null);

    // Jamais de formulaire de login dans un cadre : l'écran de reconnexion rend la
    // fenêtre du haut à l'app parente.
    if (isEmbedded()) {
      setSessionExpired(true);
      return;
    }

    const returnTo = encodeURIComponent(`${window.location.origin}/`);
    window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}`);
  };

  // A 401 once signed in means the session died elsewhere. The user asked for
  // nothing, so no logout is replayed on an already-revoked session: purge and
  // offer a way back. Armed only once authenticated — the mount probe below owns
  // the 401 that simply means "no session yet", and answers it with a silent login.
  const handleSessionLost = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    if (isEmbedded()) {
      // Embed contract: ONE silent attempt; definitive failure comes back as
      // ?connect_error → placeholder. Takeover belongs to the parent shell.
      const returnTo = encodeURIComponent(window.location.href);
      window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}&silent=1`);
      return;
    }
    const returnTo = encodeURIComponent(window.location.href);
    window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}`);
  };

  // 401 bridge: wired for everyone, embedded included — it is what lets a user
  // action inside a frame trigger the silent recovery above.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    setSessionExpiredHandler(handleSessionLost);
    return () => setSessionExpiredHandler(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Focus-only watch (auth-kit 0.10.0): user action + tab focus. When embedded, no
  // watch — the parent shell judges; the frame heals itself through the 401 bridge.
  useEffect(() => {
    if (!isAuthenticated || isEmbedded()) return undefined;
    return startSessionWatch({
      probeUrl: `${API_BASE_URL}/api/me`,
      onSessionLost: handleSessionLost,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check authentication status and fetch user data on mount
  useEffect(() => {
    // Purge du jeton de l'ancienne auth Basic : base64(login:motdepasse), le mot de
    // passe en clair, décodable. Insights ne l'a jamais lu, mais des apps non migrées
    // (Weekly) le posent encore sur tout .globe-groupe.com — on le supprime au passage
    // pour nettoyer les navigateurs, tant que ces apps existent.
    localStorage.removeItem('authToken');
    // Domaine configuré (prod), registrable courant (dev/previews) et host-only :
    // un attribut domain qui ne correspond pas = suppression qui no-op en silence.
    const registrable = '.' + window.location.hostname.split('.').slice(-2).join('.');
    for (const domain of new Set([COOKIE_DOMAIN, registrable, null])) {
      document.cookie = `authToken=; path=/; max-age=-1${domain ? `; domain=${domain}` : ''}`;
    }

    // ?connect_error = failed silent login (no SSO session): reconnect screen in
    // an iframe, interactive login top-level. consumeConnectError cleans the URL.
    const connectError = consumeConnectError();
    if (connectError) {
      if (isEmbedded()) {
        setSessionExpired(true);
        setIsAuthenticating(false);
        return;
      }
      const returnTo = encodeURIComponent(window.location.href);
      window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}`);
      return;
    }

    const checkAuth = async () => {
      try {
        const userData = await baseFetchWithAuth(
          `api/me`,
          {},
          async (resOrErr) => {
            // Seule une absence de session avérée (401 portant le challenge
            // `Session`) part chercher un token chez Connect. Tout le reste —
            // 403 unknown_user, 429 (aller redemander un token au service qui
            // nous rate-limite ne fait qu'aggraver la saturation), 5xx, erreur
            // de parsing — s'affiche sans redirection.
            const lost = typeof resOrErr?.headers?.get === 'function' && isSessionLoss(resOrErr);
            if (!lost) {
              setAuthError(`Authentication failed (HTTP ${resOrErr?.status ?? '?'}).`);
              return;
            }

            // In an iframe, attempt a SILENT login (silent=1, no form in the
            // frame); on failure the callback bounces back with ?connect_error.
            const silent = isEmbedded() ? '&silent=1' : '';
            const returnTo = encodeURIComponent(window.location.href);
            window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}${silent}`);
          }
        );

        setCurrentUser(userData);
        setIsAuthenticated(true);
        setAuthError(null);
        setTimeout(() => setIsAuthenticating(false), 250);
      } catch (e) {
        // Redirect already triggered in onError; stop the loader as a fallback.
        setIsAuthenticated(false);
        setCurrentUser(null);
        setIsAuthenticating(false);
      }
    };

    checkAuth();
  }, []);

  // SSO session dead in an iframe: the button sends the TOP window to the parent
  // app (the user's click grants the iframe that right).
  if (sessionExpired) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4 max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <p className="text-lg font-medium text-gray-700 dark:text-gray-200">Session expirée</p>
          {/* Reloads THE FRAME: resets the memoised connect_error, hence a fresh
              silent attempt. Never window.top: ejecting the user from their cockpit
              is not a decision that belongs to a frame. */}
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticating) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        logout={logout}
        fetchWithAuth={fetchWithAuth}
        authError={authError}
      />
    </Router>
  );
}

export default App;
